<template>
  <div
    class="c-form-item c-custom-select"
    :class="[
      { 'c-form-item--horizontal': horizontal },
      fluid ? 'c-form-item--fluid' : '',
      localError ? 'c-form-item--error' : '',
    ]"
  >
    <v-label v-if="hasSlotData('label')">
      <slot name="label"></slot>
    </v-label>
    <div
      class="c-form-element multiselect-parent"
      :class="{ 'u-fullwidth': fluid }"
    >
      <div class="u-flex-set o-gutter-tiny">
        <multiselect
          v-if="optionTag"
          v-model="localValue"
          :options="options"
          :multiple="multiple"
          :preserve-search="true"
          :placeholder="placeholder"
          :custom-label="nameWithAltKey"
          :label="searchLabel"
          :track-by="trackBy"
          :loading="isLoading"
          :allow-empty="false"
          @input="onChange"
          :disabled="readOnly"
          @search-change="onSearchChange"
          :internal-search="internalSearch"
          :close-on-select="closeOnSelect"
        >
          <template slot="tag" slot-scope="props">
            <span class="custom__tag">
              <span>
                <slot name="tagName">{{ props.option[optionName] }}</slot>
              </span>
              <button
                type="button"
                class="custom__remove"
                @click="props.remove(props.option)"
              >
                <svgicon name="close"></svgicon>
              </button>
            </span>
          </template>
          <template
            v-if="optionTag && props.option[optionTag]"
            slot="option"
            slot-scope="props"
          >
            {{ props.option[optionName] }} ({{ props.option[optionTag] }})
          </template>
          <template v-else slot="option" slot-scope="props">
            {{ props.option[optionName] }}
          </template>
        </multiselect>
        <multiselect
          v-else
          v-model="localValue"
          :options="options"
          :multiple="multiple"
          :preserve-search="true"
          :placeholder="placeholder"
          :label="searchLabel"
          :track-by="trackBy"
          :loading="isLoading"
          :allow-empty="false"
          @input="onChange"
          :disabled="readOnly"
          @search-change="onSearchChange"
          :internal-search="internalSearch"
          :close-on-select="closeOnSelect"
        >
          <template slot="tag" slot-scope="props">
            <span class="custom__tag">
              <span>
                <slot name="tagName">{{ props.option[optionName] }}</slot>
              </span>
              <button
                type="button"
                class="custom__remove"
                @click="props.remove(props.option)"
              >
                <svgicon name="close"></svgicon>
              </button>
            </span>
          </template>
          <template slot="option" slot-scope="props">
            {{ props.option[optionName] }}
          </template>
        </multiselect>
        <slot name="action"></slot>
      </div>
      <slot name="suffix"></slot>
    </div>
    <v-error v-if="localError">
      <slot name="error">{{ localError }}</slot>
    </v-error>
  </div>
</template>

<script>
/**
 * Custom Select Component
 * Required fields: value, options, optionName, optionValue
 * Value: Always expects an Object or an Array with Objects
 * Deprecated: Selected cannot be used anymore, instead use v-model for getter and setter
 * Note: If you need a different output, use a computed value in the parent
 * Note: If you keep getting [Object, Object] in the select, try using the searchLabel
 */
import Vue from "vue";
import VLabel from "./Label.vue";
import VError from "./Error.vue";
import Multiselect from "vue-multiselect";

export default {
  props: {
    value: { type: [Object, Array], required: true },
    options: { type: Array, required: true },
    optionName: { type: String, required: true },
    optionValue: { type: String, required: true },
    trackBy: String,
    placeholder: String,
    searchLabel: String,
    disabled: Boolean,
    horizontal: { type: Boolean, default: true },
    fluid: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    error: String,
    readOnly: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: true },
    internalSearch: { type: Boolean, default: true },
    closeOnSelect: { type: Boolean, default: true },
    optionTag: String,
  },
  components: {
    VLabel,
    VError,
    Multiselect,
  },
  data() {
    return {
      localError: this.error,
    };
  },
  watch: {
    error() {
      this.localError = this.error;
    },
  },
  computed: {
    localValue: {
      get() {
        return Object.values(this.value).length ? this.value : null;
      },
      set(value) {
        return value;
      },
    },
  },
  methods: {
    onChange(value) {
      this.$emit("input", value);
      this.$emit("change", value);
      if (this.multiple) {
        this.$emit("updated-selected-options", value);
      }
    },
    onSearchChange(value) {
      this.$emit("search-change", value);
    },
    hasSlotData(name) {
      if (name) {
        return this.$slots[name];
      }
      return this.$slots.default;
    },
    nameWithAltKey({ Name, AltKey }) {
      if (AltKey && Name) {
        return `${Name} (${AltKey})`;
      }
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/scss/private";
/*@import "~vue-multiselect/dist/vue-multiselect.min.css";*/

/**
     * Fluid variation
     */
.c-form-item--fluid {
  .multiselect {
    width: 100%;
  }
}

/**
     * Select arrow
     */
.multiselect {
  position: relative;
}

.multiselect__select {
  &:after {
    z-index: 3;
    right: em($global-spacing-unit);
    @include rfs($font-size-tiny);
    content: "▼";
    position: absolute;
    top: 50%;
    transform: translateY(-50%) scaleY(0.65);
  }
}

/**
     * When select element is in focus
     */
.multiselect--active {
  .multiselect__tags {
    position: relative;
    z-index: 2;
    background-color: white;
    @include focus();
  }
}

$select-padding: em($global-spacing-unit-tiny * 1.5);
$select-gutter: em($global-spacing-unit-tiny * 0.5);

/**
     * Select input
     */
.multiselect__tags {
  @include c-input();
  line-height: inherit;
  width: 100%;
  display: block;
  padding: $select-padding;
  padding-bottom: $select-padding - $select-gutter;

  > * {
    &:not(:last-child) {
      margin-right: $select-gutter;
    }
  }
}

/**
     * Select, selected tags wrapper
     */
.multiselect__tags-wrap {
  display: inline;
  > * {
    margin-bottom: $select-gutter;
    &:not(:last-child) {
      margin-right: $select-gutter;
    }
  }
}

/**
     * Flexbox fix to prevent overflow
     */
.multiselect-parent {
  min-width: 0;
  .block--select & {
    width: 45% !important;
    margin-left: 5%;
  }
}

$select-remove-size: em(18px);
$select-tag-padding-side: em(6px);
.custom__tag {
  display: inline-flex;
  padding: em(4px) $select-tag-padding-side;
  background-color: palette(color-neutral, light);
  position: relative;
  padding-right: $select-tag-padding-side * 2 + $select-remove-size;
  max-width: calc(100% - 30px);
}

.custom__remove {
  position: absolute;
  color: $color-neutral;
  right: $select-tag-padding-side;
  top: em(4px);
  background-color: transparent;
  border: none;
  border-radius: 50%;
  padding: em(0);
  width: $select-remove-size;
  height: $select-remove-size;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    color: $color-base;
  }
}

/**
     * Select input element
     */
.multiselect__input {
  border: none;
  background-color: transparent;
  font: inherit;
  padding: 0;
  margin-bottom: $select-padding/3;

  .c-contact-form-occasion &,
  .block--select & {
    width: 100% !important;
  }

  &:focus {
    width: 90% !important;
    outline: none;

    .c-target-list__select & {
      width: 100px !important;
    }
  }
}

/**
     * Select dropdown
     */
.multiselect__content {
  margin: 0;
  list-style: none;
  background-color: white;
  position: absolute;
  width: 100%;
  top: calc(100% + 3px);
  box-shadow: 0 0 0 3px $color-neutral;
  padding: em($global-spacing-unit-small) 0;
  max-height: 400px;
  overflow-y: auto;
}

.multiselect__content-wrapper {
  position: relative;
  z-index: 100002;
}

.multiselect__element {
  display: block;
  cursor: pointer;

  span {
    padding: em(4px) em($global-spacing-unit);
    display: block;
  }

  &:hover {
    background-color: palette(color-neutral, light);
  }
}

.multiselect__option {
  padding: em(4px) em($global-spacing-unit);
  display: block;
}
</style>



// WEBPACK FOOTER //
// src/components/form/CustomSelect.vue